/**
 * Editing an event after it is created (GH-862).
 *
 * The owner's rule (2026-10-03): every detail is editable as long as that is reasonable and
 * possible. The server decides what is reasonable — these shapes carry the request, the current
 * values, the reasons a field is locked, and how many people a change of date, time or place
 * reaches — and the console and the phone draw what the server answers.
 *
 * Nothing here is money a donor pays. The goal is a public figure the thermometer measures
 * against; changing it never rewrites a gift, a pledge or a ledger row. Ticket prices have their
 * own route (`PATCH …/ticket-tiers/:tierId`, GH-830) where sold orders keep what they paid.
 *
 * **The platform never tells attendees about a change** (owner, 2026-10-03): the organizer handles
 * that communication. A change of date, time or venue that reaches ticket holders is saved only
 * after the organizer confirms a warning that says so, and the warning points at the attendee list.
 */
import { z } from "zod";
import { minorUnitsSchema } from "./v1.js";
/** The flyer's own words: one short paragraph printed under the event name (GH-862). */
export const EVENT_FLYER_TEXT_MAX = 280;
/** The online link's longest form (GH-866); migration 0104 holds the same bound. */
export const EVENT_ONLINE_URL_MAX = 500;
/**
 * Why this online link cannot be saved, or null when it can (GH-866). One rule for the server,
 * the console and (mirrored) the phone: an absolute `https://` URL with a dotted host, no
 * credentials, no spaces, at most `EVENT_ONLINE_URL_MAX` characters. The link is published on
 * public pages, so a `javascript:` or `http:` link is refused rather than escaped later.
 */
export function onlineUrlProblem(raw) {
    const text = raw.trim();
    const shape = "Use a full link that starts with https://, like https://example.org/live.";
    if (text.length > EVENT_ONLINE_URL_MAX) {
        return `Keep the online link to ${EVENT_ONLINE_URL_MAX} characters.`;
    }
    if (!/^https:\/\/\S+$/i.test(text))
        return shape;
    let url;
    try {
        url = new URL(text);
    }
    catch {
        return shape;
    }
    if (url.protocol !== "https:" || !url.hostname.includes("."))
        return shape;
    if (url.username !== "" || url.password !== "") {
        return "Leave any user name or password out of the link.";
    }
    return null;
}
/** An IANA zone name ("America/Chicago"). The server checks it against the runtime's zone list. */
export const eventTimeZoneSchema = z.string().min(1).max(64);
/** The editable details of an event, as the editor loads them. */
export const eventDetailsSchema = z.object({
    name: z.string().min(1).max(160),
    /** What the money is for (SPEC.md §6.5). A material field: changing it bumps the version. */
    purpose: z.string().min(1).max(300),
    startsAt: z.string().nullable(),
    endsAt: z.string().nullable(),
    /** The zone the organizer means the times in; the flyer prints times in it. Null = not set. */
    timeZone: z.string().nullable(),
    /** Free-text place ("Main hall"), never geocoded. */
    location: z.string().nullable(),
    /** The venue's postal address, geocoded best-effort by the server. */
    address: z.string().nullable(),
    addressGeocoded: z.boolean(),
    goalAmountMinor: minorUnitsSchema,
    suggestedAmountsMinor: z.array(minorUnitsSchema),
    defaultAmountMinor: minorUnitsSchema.nullable(),
    flyerText: z.string().nullable(),
    /** Where people join from afar, for a hybrid event (GH-866). Defaulted for an older server. */
    onlineUrl: z.string().nullable().default(null),
});
/**
 * `POST /v1/events/:eventId/details` — send only what changed.
 *
 * `confirmAffected` is the organizer's answer to the warning "42 tickets sold to 31 people — they
 * won't be told automatically": a change of date, time or venue on an event with valid tickets,
 * sent invitations or app check-ins is refused without it. Nothing is emailed either way.
 */
export const updateEventDetailsSchema = z
    .object({
    name: z.string().trim().min(1).max(160).optional(),
    purpose: z.string().trim().min(1).max(300).optional(),
    startsAt: z.string().datetime({ offset: true }).nullable().optional(),
    endsAt: z.string().datetime({ offset: true }).nullable().optional(),
    timeZone: eventTimeZoneSchema.nullable().optional(),
    location: z.string().trim().max(200).nullable().optional(),
    address: z.string().trim().max(300).nullable().optional(),
    goalAmountMinor: minorUnitsSchema.optional(),
    suggestedAmountsMinor: z.array(minorUnitsSchema).max(8).optional(),
    defaultAmountMinor: minorUnitsSchema.nullable().optional(),
    flyerText: z.string().trim().max(EVENT_FLYER_TEXT_MAX).nullable().optional(),
    /** `https://` only; the server checks the whole rule (`onlineUrlProblem`). Not a venue move. */
    onlineUrl: z.string().trim().max(EVENT_ONLINE_URL_MAX).nullable().optional(),
    confirmAffected: z.boolean().optional(),
})
    .strict();
/**
 * Who a change of date, time or place reaches. Counts only — never a name or an address. The
 * warning reads them; nobody they count is contacted by the platform.
 */
export const eventChangeAudienceSchema = z.object({
    /** Distinct buyers holding at least one admission that still admits. */
    ticketHolders: z.number().int().min(0),
    /** Admissions that still admit (issued or checked in, not voided). */
    tickets: z.number().int().min(0),
    /** Invitations that were actually sent. */
    invitees: z.number().int().min(0),
    /** Donors who checked in to the event on the app. */
    checkIns: z.number().int().min(0),
});
/** True when a change of date, time or place reaches nobody: no ticket, invitation or check-in. */
export const audienceIsEmpty = (audience) => audience.tickets === 0 && audience.invitees === 0 && audience.checkIns === 0;
/**
 * The warning's first sentence, composed once for the console and (mirrored) the phone:
 * "42 tickets sold to 31 people", then invitations and check-ins when there are any.
 */
export function audienceSentence(audience) {
    const plural = (count, one, many) => `${count.toLocaleString("en-US")} ${count === 1 ? one : many}`;
    const parts = [];
    if (audience.tickets > 0) {
        parts.push(`${plural(audience.tickets, "ticket", "tickets")} sold to ${plural(audience.ticketHolders, "person", "people")}`);
    }
    if (audience.invitees > 0)
        parts.push(plural(audience.invitees, "invitation", "invitations"));
    if (audience.checkIns > 0)
        parts.push(plural(audience.checkIns, "app check-in", "app check-ins"));
    if (parts.length === 0)
        return "Nobody holds a ticket yet";
    if (parts.length === 1)
        return parts[0];
    return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}
/** Why a group of fields cannot be edited right now, in words the editor shows; null = editable. */
export const eventEditLocksSchema = z.object({
    /** Everything: an archived or suspended event. */
    all: z.string().nullable(),
    /** Start time. */
    startsAt: z.string().nullable(),
    /** End time. */
    endsAt: z.string().nullable(),
});
/** `GET /v1/events/:eventId/details`. */
export const eventDetailsResponseSchema = z.object({
    eventId: z.string().uuid(),
    /** Effective status (GH-800). */
    status: z.string(),
    version: z.number().int(),
    isGeneralFund: z.boolean(),
    details: eventDetailsSchema,
    locks: eventEditLocksSchema,
    /** Shown beside the editor after the event has ended; null otherwise. */
    note: z.string().nullable(),
    audience: eventChangeAudienceSchema,
});
/** `POST /v1/events/:eventId/details` answers the new state plus what the save did. */
export const updateEventDetailsResponseSchema = eventDetailsResponseSchema.extend({
    /** Field names that changed; empty when the request changed nothing. */
    changed: z.array(z.string()),
    /** True when the change was material (purpose or timing) and the event's version moved. */
    versionBumped: z.boolean(),
});
/** `POST /v1/events/:eventId/speaker-photos` answers the stored photo's id and public URL. */
export const speakerPhotoResponseSchema = z.object({
    photoId: z.string().uuid(),
    photoUrl: z.string(),
});
/** The public URL of a speaker photo. Immutable: a new photo is a new id. */
export const speakerPhotoUrl = (eventId, photoId) => `/v1/public/events/${eventId}/speaker-photos/${photoId}`;
/* ------------------------------------------------------------ the message for attendees */
/** The event's public page, as attendees open it: `<origin>/e/<eventId>`. */
export const publicEventUrl = (origin, eventId) => `${origin.replace(/\/+$/, "")}/e/${eventId}`;
const TO_BE_ANNOUNCED = "to be announced";
function zonedFormatter(timeZone) {
    const options = {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZoneName: "short",
    };
    try {
        return new Intl.DateTimeFormat("en-US", { ...options, timeZone });
    }
    catch {
        return new Intl.DateTimeFormat("en-US", { ...options, timeZone: "UTC" });
    }
}
function whenParts(iso, timeZone) {
    const parts = new Map(zonedFormatter(timeZone)
        .formatToParts(new Date(iso))
        .map((part) => [part.type, part.value]));
    const part = (type) => parts.get(type) ?? "";
    // Assembled from parts so the spacing is plain ASCII whatever the ICU build puts before "PM".
    return {
        date: `${part("weekday")}, ${part("month")} ${part("day")}, ${part("year")}`,
        time: `${part("hour")}:${part("minute")} ${part("dayPeriod").toUpperCase()}`,
        zone: part("timeZoneName"),
    };
}
/** "Saturday, October 17, 2026, 6:00 PM to 9:00 PM CDT", or "to be announced". */
function whenText(startsAt, endsAt, timeZone) {
    if (startsAt === null)
        return TO_BE_ANNOUNCED;
    const start = whenParts(startsAt, timeZone);
    if (endsAt === null)
        return `${start.date}, ${start.time} ${start.zone}`;
    const end = whenParts(endsAt, timeZone);
    return end.date === start.date
        ? `${start.date}, ${start.time} to ${end.time} ${end.zone}`
        : `${start.date}, ${start.time} to ${end.date}, ${end.time} ${end.zone}`;
}
const venueText = (snapshot) => [snapshot.location, snapshot.address]
    .map((part) => part?.trim() ?? "")
    .filter((part) => part !== "")
    .join(", ") || TO_BE_ANNOUNCED;
/**
 * The message an organizer can copy and send attendees themselves after moving the date, the time
 * or the venue (GH-866). The platform still sends nothing (GH-862). Plain and neutral: the event's
 * name, what changed (new, then what it was), and the event's page. Never an amount or a name.
 * The phone composes the same words (`AdminAttendeeMessage.swift`), held to
 * `ATTENDEE_MESSAGE_PARITY_CASES`.
 */
export function attendeeChangeMessage(input) {
    const { before, after, timeZone } = input;
    const lines = [`An update about ${input.eventName.trim()}`];
    const beforeWhen = whenText(before.startsAt, before.endsAt, timeZone);
    const afterWhen = whenText(after.startsAt, after.endsAt, timeZone);
    if (beforeWhen !== afterWhen) {
        lines.push("", `New date and time: ${afterWhen} (was ${beforeWhen}).`);
    }
    const beforeVenue = venueText(before);
    const afterVenue = venueText(after);
    if (beforeVenue !== afterVenue) {
        lines.push("", `New venue: ${afterVenue} (was ${beforeVenue}).`);
    }
    if (input.eventUrl)
        lines.push("", `Event page: ${input.eventUrl}`);
    return lines.join("\n");
}
const PARITY_BEFORE = {
    startsAt: "2026-10-16T23:00:00.000Z",
    endsAt: "2026-10-17T02:00:00.000Z",
    location: "Fellowship room",
    address: null,
};
/**
 * Worked examples, exported to the phone (`pnpm ios:fixtures`) so the Swift composer is held to
 * these exact words. US zones only: zone abbreviations elsewhere differ between ICU and Foundation.
 */
export const ATTENDEE_MESSAGE_PARITY_CASES = [
    {
        input: {
            eventName: "Spring Gala",
            timeZone: "America/Chicago",
            eventUrl: "https://give.example.org/e/11111111-1111-4111-8111-111111111111",
            before: PARITY_BEFORE,
            after: {
                ...PARITY_BEFORE,
                startsAt: "2026-10-17T23:00:00.000Z",
                endsAt: "2026-10-18T02:00:00.000Z",
            },
        },
        expected: [
            "An update about Spring Gala",
            "",
            "New date and time: Saturday, October 17, 2026, 6:00 PM to 9:00 PM CDT (was Friday, October 16, 2026, 6:00 PM to 9:00 PM CDT).",
            "",
            "Event page: https://give.example.org/e/11111111-1111-4111-8111-111111111111",
        ].join("\n"),
    },
    {
        input: {
            eventName: "Spring Gala",
            timeZone: "America/New_York",
            eventUrl: null,
            before: PARITY_BEFORE,
            after: { startsAt: "2026-10-18T00:00:00.000Z", endsAt: null, location: null, address: null },
        },
        expected: [
            "An update about Spring Gala",
            "",
            "New date and time: Saturday, October 17, 2026, 8:00 PM EDT (was Friday, October 16, 2026, 7:00 PM to 10:00 PM EDT).",
            "",
            "New venue: to be announced (was Fellowship room).",
        ].join("\n"),
    },
    {
        input: {
            eventName: "Night Watch",
            timeZone: "America/Chicago",
            eventUrl: "https://give.example.org/e/22222222-2222-4222-8222-222222222222",
            before: { ...PARITY_BEFORE, startsAt: null, endsAt: null },
            after: {
                startsAt: "2026-10-17T03:00:00.000Z",
                endsAt: "2026-10-17T08:00:00.000Z",
                location: "Main hall",
                address: "123 Main St, Springfield, IL",
            },
        },
        expected: [
            "An update about Night Watch",
            "",
            "New date and time: Friday, October 16, 2026, 10:00 PM to Saturday, October 17, 2026, 3:00 AM CDT (was to be announced).",
            "",
            "New venue: Main hall, 123 Main St, Springfield, IL (was Fellowship room).",
            "",
            "Event page: https://give.example.org/e/22222222-2222-4222-8222-222222222222",
        ].join("\n"),
    },
];
//# sourceMappingURL=event-editing.js.map